import { useEffect, useMemo, useRef, useState } from "react";
import { getAntiforgeryToken } from "../api/http";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { FieldError, SectionMessage, Toast } from "../ui/feedback";
import { ChatIcon } from "../ui/icons/Icon";
// El motor de vistas de tabla del portal actual (copia de Bheli.Web.Feedback/Reports/ReportViews.razor.js): pone el botón
// de columnas, aplica columnas, orden y anchos a cada table[data-report-id] sin rehacer su DOM y llama al host con los
// mismos nombres que hoy llama a .NET (OpenReportAsync, RestoreReportAsync, ColumnOrderChanged, ...).
import * as engine from "./ReportViews.legacy.js";

type Column = { id: string; label: string; required: boolean; isSystem: boolean };
type ColumnSetting = { id: string; visible: boolean; position: number; width: number | null };
type Configuration = {
  schemaVersion: number;
  columns: ColumnSetting[];
  sort: { id: string; direction: string } | null;
  filters: Record<string, string[]>;
};
type Table = { reportId: string; columns: Column[]; baseConfiguration: Configuration; canSort: boolean };
type SavedView = { id: string; name: string; isDefault: boolean; configuration: string; revision: number };
type Result = { views: SavedView[]; savedId: string | null; error: string | null };
type Failure = { error: string; recoveryUrl?: string; recoveryLabel?: string } | null;

const emptyConfiguration: Configuration = { schemaVersion: 1, columns: [], sort: null, filters: {} };

function deserialize(json: string): Configuration {
  try {
    const value = JSON.parse(json) as Configuration;
    return value?.schemaVersion === 1 && Array.isArray(value.columns) && value.filters ? value : emptyConfiguration;
  } catch {
    return emptyConfiguration;
  }
}

/** Una columna nueva no altera una vista elegida; las obligatorias siguen visibles (Reconcile del portal actual). */
function reconcile(table: Table, value: Configuration): Configuration {
  const known = new Map(table.columns.map((column) => [column.id, column]));
  let columns = value.columns
    .filter((column, index, all) => known.has(column.id) && all.findIndex((other) => other.id === column.id) === index)
    .sort((a, b) => a.position - b.position)
    .map((column) => ({ ...column, visible: column.visible || known.get(column.id)!.required }));
  for (const column of table.columns)
    if (!columns.some((setting) => setting.id === column.id))
      columns.push({ id: column.id, visible: column.required, position: columns.length, width: null });
  if (table.reportId.startsWith("chat.statistics."))
    columns = [...columns].sort(
      (a, b) => Number(known.get(b.id)!.required) - Number(known.get(a.id)!.required) || a.position - b.position,
    );
  return {
    ...value,
    columns: columns.map((column, index) => ({ ...column, position: index })),
    sort: value.sort && known.has(value.sort.id) ? value.sort : null,
  };
}

/** Comprueba el contexto vivo de la pantalla antes de aplicar (verifyContext de CompanyReportViews.razor.js). */
async function verifyContext(report: string, owner: string): Promise<string | null> {
  const endpoint =
    report === "chat.contacts"
      ? "/api/portal/contacts/context"
      : report === "company.tagcatalogworkspace.t1"
        ? "/api/portal/contacts/catalog/context"
        : report.startsWith("chat.statistics.")
          ? "/api/portal/chat-reports/context"
          : null;
  if (!endpoint) return null;
  try {
    const response = await fetch(endpoint, { credentials: "same-origin", cache: "no-store", redirect: "error" });
    if (response.status === 401) return "SESSION_INVALID";
    if (response.status === 403) return "DENIED";
    if (!response.ok) return "UNAVAILABLE";
    const context = await response.json();
    if (`${context.userId}:${context.organizationId}` !== owner) return "TENANT_CHANGED";
    if (context.access?.canRead !== true) return "DENIED";
    return null;
  } catch {
    return "UNAVAILABLE";
  }
}

async function api(method: string, report: string, body?: unknown, id?: string, revision?: number): Promise<Result> {
  const url = `/api/portal/report-views/${encodeURIComponent(report)}${id ? `/${id}?revision=${revision}` : ""}`;
  const response = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-CSRF-TOKEN": getAntiforgeryToken() ?? "" },
    body: body ? JSON.stringify(body) : undefined,
  });
  return response.json().catch(() => ({ views: [], savedId: null, error: "UNAVAILABLE" }));
}

/** Host de las vistas de tabla (CompanyReportViews + ReportViews): un solo diálogo para todas las tablas de la página. */
export function ReportViews({
  owner,
  labels,
  path,
}: {
  owner: string | null;
  labels: Record<string, string>;
  path: string;
}) {
  const L = (key: string) => labels[`R3_VIEW_${key}`] ?? `⟦R3_VIEW_${key}⟧`;
  const C = (key: string) => labels[`C1_${key}`] ?? `[C1_${key}]`;
  const root = useRef<HTMLDivElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [table, setTable] = useState<Table | null>(null);
  const [configuration, setConfiguration] = useState<Configuration>(emptyConfiguration);
  const [views, setViews] = useState<SavedView[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [nameInvalid, setNameInvalid] = useState(false);
  const [failure, setFailure] = useState<Failure>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [receipt, setReceipt] = useState(0);
  const widths = useRef(new Map<string, Map<string, number | null>>());
  const state = useRef({ table, configuration, selectedId, views });
  useEffect(() => {
    state.current = { table, configuration, selectedId, views };
  });
  // El motor se enlaza una vez por dueño: llama siempre a la versión actual de orderChanged.
  const latestOrderChanged = useRef<(listed: string[]) => void>(() => undefined);
  const isContacts = table?.reportId === "chat.contacts";
  const isTags = table?.reportId === "company.tagcatalogworkspace.t1";

  const withWidths = (value: Configuration, overrides?: Map<string, number | null>) =>
    !overrides
      ? value
      : {
          ...value,
          columns: value.columns.map((column) =>
            overrides.has(column.id) ? { ...column, width: overrides.get(column.id)! } : column,
          ),
        };

  const failed = (error: string | null) => {
    if (!error) {
      setFailure(null);
      return;
    }
    if (error === "SESSION_INVALID" || error === "TENANT_CHANGED") {
      setFailure({
        error: L(error),
        recoveryUrl: error === "SESSION_INVALID" ? `/login?ReturnUrl=${encodeURIComponent(path)}` : path,
        recoveryLabel: labels[error === "SESSION_INVALID" ? "C1_SIGN_IN" : "C1_RELOAD"],
      });
    } else setFailure({ error: L(error) });
  };

  const applyNow = async (next: Configuration, filters = false, automatic = false, id = state.current.selectedId) => {
    const current = state.current.table;
    if (!current) return;
    const url = await engine.apply(current.reportId, next, filters, id, automatic);
    if (url) window.location.assign(url);
  };

  useEffect(() => {
    if (!owner || !root.current || !dialog.current) return;
    const reference = {
      async invokeMethodAsync(method: string, ...args: unknown[]): Promise<unknown> {
        switch (method) {
          case "RefreshLabelsAsync":
            return true; // Los textos ya vienen del idioma de la página.
          case "RestoreReportAsync": {
            const description = args[0] as Table;
            const result = await api("GET", description.reportId);
            if (result.error) {
              engine.loadFailure(description.reportId, L(result.error));
              return undefined;
            }
            const requested = engine.selectedView(description.reportId);
            const saved =
              requested === null
                ? result.views.find((view) => view.isDefault)
                : result.views.find((view) => view.id === requested);
            if (saved) {
              const value = reconcile(description, deserialize(saved.configuration));
              await engine.apply(description.reportId, value, false);
              const url = engine.defaultFiltersUrl(description.reportId, value, saved.id);
              if (url) window.location.assign(url);
            } else if (requested !== null)
              await engine.apply(description.reportId, description.baseConfiguration, false, null, true);
            return undefined;
          }
          case "OpenReportAsync": {
            const description = args[0] as Table;
            const same = state.current.table?.reportId === description.reportId;
            setTable(description);
            setFailure(null);
            setNameInvalid(false);
            setBusy(true);
            const result = await api("GET", description.reportId);
            setBusy(false);
            failed(result.error);
            const list = result.error ? state.current.views : result.views;
            if (!result.error) setViews(result.views);
            if (!same) {
              const requested = engine.selectedView(description.reportId);
              const selected =
                requested === null ? list.find((view) => view.isDefault) : list.find((view) => view.id === requested);
              setSelectedId(selected?.id ?? null);
              setName(selected?.name ?? "");
              setIsDefault(selected?.isDefault ?? false);
              let value = reconcile(
                description,
                selected ? deserialize(selected.configuration) : description.baseConfiguration,
              );
              const overrides = widths.current.get(description.reportId);
              setDirty(!!overrides?.size);
              if (overrides?.size) value = withWidths(value, overrides);
              setConfiguration(value);
            } else setConfiguration(reconcile(description, state.current.configuration));
            requestAnimationFrame(() => engine.open(dialog.current!));
            return undefined;
          }
          case "ColumnWidthChanged": {
            const [report, id, width] = args as [string, string, number | null];
            const map = widths.current.get(report) ?? new Map<string, number | null>();
            map.set(id, width === null ? null : Math.min(800, Math.max(40, Math.round(width))));
            widths.current.set(report, map);
            if (state.current.table?.reportId === report) {
              setConfiguration((value) => withWidths(value, map));
              setDirty(true);
            }
            return undefined;
          }
          case "ColumnOrderChanged":
            latestOrderChanged.current(args[0] as string[]);
            return undefined;
          default:
            return undefined;
        }
      },
    };
    engine.bind(root.current, dialog.current, reference, owner, L("CONFIGURE"));
    const element = root.current;
    return () => engine.dispose(element);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner]);

  useEffect(() => {
    if (root.current) engine.labels(root.current, L("CONFIGURE"));
  });

  const orderChanged = (listed: string[]) => {
    const { table: current, configuration: value } = state.current;
    if (!current) return;
    const system = new Set(current.columns.filter((column) => column.isSystem).map((column) => column.id));
    let ids = listed;
    if (system.size > 0 && !listed.some((id) => system.has(id))) {
      const queue = [...listed];
      ids = value.columns.map((column) => (system.has(column.id) ? column.id : (queue.shift() ?? column.id)));
    }
    if (ids.length !== value.columns.length || new Set(ids).size !== ids.length) return;
    const map = new Map(value.columns.map((column) => [column.id, column]));
    if (ids.some((id) => !map.has(id))) return;
    if (current.reportId.startsWith("chat.statistics.")) {
      const required = new Set(current.columns.filter((column) => column.required).map((column) => column.id));
      ids = [...ids].sort((a, b) => Number(required.has(b)) - Number(required.has(a)));
    }
    const next = { ...value, columns: ids.map((id, index) => ({ ...map.get(id)!, position: index })) };
    setConfiguration(next);
    setDirty(true);
    void applyNow(next);
  };
  useEffect(() => {
    latestOrderChanged.current = orderChanged;
  });

  const validate = async () => {
    if (!table || !owner) return true;
    setBusy(true);
    setSuccess(null);
    const error = await verifyContext(table.reportId, owner);
    setBusy(false);
    failed(error);
    return error === null;
  };

  const options = useMemo(
    () => [
      { value: "", label: L("BASE") },
      ...views.map((view) => ({ value: view.id, label: view.name + (view.isDefault ? ` · ${L("DEFAULT")}` : "") })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [views, labels],
  );

  const select = async (value: string) => {
    if (!table || !(await validate())) return;
    const view = views.find((item) => item.id === value) ?? null;
    widths.current.delete(table.reportId);
    setSelectedId(view?.id ?? null);
    setName(view?.name ?? "");
    setIsDefault(view?.isDefault ?? false);
    setFailure(null);
    setDirty(false);
    const next = reconcile(table, view ? deserialize(view.configuration) : table.baseConfiguration);
    setConfiguration(next);
    await applyNow(next, true, view === null, view?.id ?? null);
  };

  const visibility = async (id: string, visible: boolean) => {
    const next = {
      ...configuration,
      columns: configuration.columns.map((column) => (column.id === id ? { ...column, visible } : column)),
    };
    setConfiguration(next);
    setDirty(true);
    await applyNow(next);
  };

  // Etiquetas R1: el mismo orden [sistema(0), principal(1), movibles...] que Contactos, mismo tope.
  const move = (index: number, delta: number) => {
    if (index < 2 || index + delta < 2) return;
    const columns = [...configuration.columns];
    const [item] = columns.splice(index, 1);
    columns.splice(index + delta, 0, item!);
    orderChanged(columns.map((column) => column.id));
  };

  const restoreBase = async () => {
    if (!table || busy || !(await validate())) return false;
    setConfiguration(table.baseConfiguration);
    setSelectedId(null);
    setName("");
    setIsDefault(false);
    setDirty(table.reportId !== "chat.contacts");
    widths.current.delete(table.reportId);
    await applyNow(table.baseConfiguration, true, true, null);
    return true;
  };

  const save = async (asNew: boolean) => {
    if (busy || !table) return;
    if (!name.trim()) {
      setNameInvalid(true);
      return;
    }
    const old = asNew ? null : (views.find((view) => view.id === selectedId) ?? null);
    const value = { ...configuration, filters: engine.filters(table.reportId) };
    const view = {
      id: old?.id ?? crypto.randomUUID(),
      name: name.trim(),
      isDefault,
      configuration: JSON.stringify(value),
      revision: old?.revision ?? 0,
    };
    setBusy(true);
    setFailure(null);
    setSuccess(null);
    const result = await api("PUT", table.reportId, view);
    setBusy(false);
    if (result.error) {
      failed(result.error);
      return;
    }
    setViews(result.views);
    setSelectedId(result.savedId);
    setDirty(false);
    setSuccess(L("SAVED"));
    setReceipt((value) => value + 1);
    widths.current.delete(table.reportId);
    engine.markView(table.reportId, result.savedId);
  };

  const remove = async () => {
    if (busy || !table || !selectedId) return;
    const view = views.find((item) => item.id === selectedId);
    if (!view) return;
    setBusy(true);
    const result = await api("DELETE", table.reportId, undefined, view.id, view.revision);
    setBusy(false);
    if (result.error) {
      failed(result.error);
      return;
    }
    setViews(result.views);
    setSelectedId(null);
    setIsDefault(false);
    if (!(await restoreBase())) return;
    setDirty(false);
    setSuccess(L("DELETED"));
    setReceipt((value) => value + 1);
  };

  const fixed = (label: string) => `${C("ALWAYS_VISIBLE")}: ${label}`;
  const description = table;
  return (
    <div data-report-views-host="" ref={root}>
      <dialog
        className="report-view-dialog c1-report-window"
        data-report-id={table?.reportId}
        ref={dialog}
        aria-labelledby="report-view-title"
        tabIndex={-1}
      >
        <header>
          <button
            type="button"
            className="button ghost report-view-back"
            aria-label={L("CLOSE")}
            onClick={() => engine.close(dialog.current!)}
          >
            <ChatIcon name="arrow-left" />
          </button>
          <h2 id="report-view-title">{L("CONFIGURE")}</h2>
          <button
            type="button"
            className="button ghost report-view-close"
            aria-label={L("CLOSE")}
            onClick={() => engine.close(dialog.current!)}
          >
            <ChatIcon name="x" />
          </button>
        </header>
        {description ? (
          <>
            <div className="report-view-body">
              {failure ? (
                <>
                  <SectionMessage message={failure.error} tone="error" />
                  {failure.recoveryUrl ? (
                    <a className="button secondary" href={failure.recoveryUrl} data-enhance-nav="false">
                      {failure.recoveryLabel}
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="button secondary"
                      disabled={busy}
                      onClick={() => {
                        const current = state.current.table;
                        if (current) void reference(current);
                      }}
                    >
                      {L("RETRY")}
                    </button>
                  )}
                  {views.some((view) => view.name.trim().toLowerCase() === name.trim().toLowerCase()) ? (
                    <button
                      type="button"
                      className="button secondary"
                      disabled={busy}
                      onClick={() => {
                        const match = views.find(
                          (view) => view.name.trim().toLowerCase() === name.trim().toLowerCase(),
                        );
                        if (match) {
                          setSelectedId(match.id);
                          void save(false);
                        }
                      }}
                    >
                      {L("UPDATE")}
                    </button>
                  ) : null}
                </>
              ) : null}
              <div className={isContacts ? "field" : "report-view-picker"}>
                <span>{L("CURRENT")}</span>
                <ChoiceMenu
                  label={L("CURRENT")}
                  value={selectedId ?? ""}
                  onChange={(value) => void select(value)}
                  disabled={busy}
                  options={options}
                  searchLabel={C("SEARCH_VIEWS")}
                  clearSearchLabel={C("CLEAR_SEARCH")}
                  searchIcon={<ChatIcon name="search" />}
                  clearIcon={<ChatIcon name="x" />}
                  chevron={<ChatIcon name="chevron-down" />}
                  check={<ChatIcon name="check" />}
                />
              </div>
              <p role="status" className={isContacts ? undefined : "report-view-state"}>
                {dirty ? L("UNSAVED") : L("SAVED_STATE")}
              </p>
              <div className="report-column-order" role="list" aria-label={L("COLUMNS")}>
                {configuration.columns.map((column, position, columns) => {
                  const definition = description.columns.find((item) => item.id === column.id)!;
                  if (isContacts || isTags) {
                    if (column.id === "selection" || (isTags && definition.isSystem)) return null;
                    // C28 1.14.4 (D15): ↑ apagada en la primera columna que se mueve y ↓ en la última; las de sistema y
                    // la fija no cuentan (en Etiquetas la última es la de acciones, que no se muestra aquí).
                    const movable = columns
                      .filter((item) => {
                        const known = description.columns.find((entry) => entry.id === item.id);
                        return item.id !== "selection" && !!known && !known.required && !known.isSystem;
                      })
                      .map((item) => item.id);
                    const firstMovable = column.id === movable[0],
                      last = column.id === movable.at(-1);
                    return (
                      <div
                        key={column.id}
                        className="report-column-row"
                        data-view-column={column.id}
                        data-column-fixed={definition.required ? "true" : undefined}
                        role="listitem"
                      >
                        {definition.required ? (
                          <>
                            <span
                              className="report-column-lock"
                              title={fixed(definition.label)}
                              aria-label={fixed(definition.label)}
                              role="img"
                            >
                              <ChatIcon name="lock-keyhole" />
                            </span>
                            <label>
                              <span>
                                {definition.label}
                                <small>{C("ALWAYS_VISIBLE")}</small>
                              </span>
                            </label>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="button ghost report-drag-handle"
                              data-column-handle=""
                              disabled={busy || definition.required}
                              aria-label={`${L("MOVE")}: ${definition.label}`}
                            >
                              <ChatIcon name="grip-vertical" />
                            </button>
                            <label>
                              <input
                                type="checkbox"
                                checked={column.visible}
                                disabled={definition.required || busy}
                                onChange={(event) => void visibility(column.id, event.target.checked)}
                              />
                              <span>{column.id === "selection" ? C("SELECTION_COLUMN") : definition.label}</span>
                            </label>
                            <button
                              type="button"
                              className="button ghost report-move"
                              disabled={firstMovable || busy}
                              aria-disabled={firstMovable ? "true" : undefined}
                              aria-label={`${L("UP")}: ${definition.label}`}
                              title={firstMovable ? `${L("UP")}: ${definition.label}` : undefined}
                              onClick={() => move(position, -1)}
                            >
                              <ChatIcon name="arrow-up" />
                            </button>
                            <button
                              type="button"
                              className="button ghost report-move"
                              disabled={last || busy}
                              aria-disabled={last ? "true" : undefined}
                              aria-label={`${L("DOWN")}: ${definition.label}`}
                              title={last ? `${L("DOWN")}: ${definition.label}` : undefined}
                              onClick={() => move(position, 1)}
                            >
                              <ChatIcon name="arrow-down" />
                            </button>
                          </>
                        )}
                      </div>
                    );
                  }
                  if (definition.isSystem) return null;
                  return (
                    <div
                      key={column.id}
                      className="report-column-row"
                      data-view-column={column.id}
                      data-column-fixed={definition.required ? "true" : undefined}
                      role="listitem"
                    >
                      {definition.required ? (
                        <span
                          className="report-column-lock"
                          title={fixed(definition.label)}
                          aria-label={fixed(definition.label)}
                          role="img"
                        >
                          <ChatIcon name="lock-keyhole" />
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="button ghost report-drag-handle"
                          data-column-handle=""
                          disabled={busy}
                          aria-label={`${L("MOVE")}: ${definition.label}`}
                          aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
                        >
                          <ChatIcon name="grip-vertical" />
                        </button>
                      )}
                      <label>
                        <input
                          type="checkbox"
                          className="report-check"
                          checked={column.visible}
                          disabled={definition.required || busy}
                          aria-keyshortcuts={definition.required ? undefined : "Alt+ArrowUp Alt+ArrowDown"}
                          onChange={(event) => void visibility(column.id, event.target.checked)}
                        />
                        <span>{definition.label}</span>
                      </label>
                    </div>
                  );
                })}
              </div>
              {description.canSort ? (
                <div className="report-sort">
                  <label className="field">
                    {L("SORT")}
                    <select
                      disabled={busy}
                      value={configuration.sort?.id ?? ""}
                      onChange={(event) => {
                        const id = event.target.value;
                        const next = { ...configuration, sort: id ? { id, direction: "asc" } : null };
                        setConfiguration(next);
                        setDirty(true);
                        void applyNow(next);
                      }}
                    >
                      <option value="">{L("ORIGINAL_ORDER")}</option>
                      {description.columns
                        .filter((column) => column.id !== "selection" && column.id !== "actions")
                        .map((column) => (
                          <option key={column.id} value={column.id}>
                            {column.label}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="field">
                    {L("DIRECTION")}
                    <select
                      disabled={busy}
                      value={configuration.sort?.direction ?? "asc"}
                      onChange={(event) => {
                        if (!configuration.sort) return;
                        const next = {
                          ...configuration,
                          sort: { ...configuration.sort, direction: event.target.value === "desc" ? "desc" : "asc" },
                        };
                        setConfiguration(next);
                        setDirty(true);
                        void applyNow(next);
                      }}
                    >
                      <option value="asc">{L("ASC")}</option>
                      <option value="desc">{L("DESC")}</option>
                    </select>
                  </label>
                </div>
              ) : null}
              <label className="field" htmlFor="report-view-name">
                {L("NAME")}
                <input
                  id="report-view-name"
                  maxLength={80}
                  disabled={busy}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setDirty(true);
                    setNameInvalid(false);
                  }}
                  aria-invalid={nameInvalid ? "true" : undefined}
                  aria-describedby="report-name-error"
                />
              </label>
              <FieldError id="report-name-error" message={nameInvalid ? L("NAME_REQUIRED") : null} />
              <button
                type="button"
                className="c1-default-switch"
                role="switch"
                aria-checked={isDefault ? "true" : "false"}
                disabled={busy}
                onClick={() => {
                  setIsDefault(!isDefault);
                  setDirty(true);
                  setNameInvalid(false);
                }}
              >
                <span className="c1-switch-track" aria-hidden="true"></span>
                <span>
                  {C("DEFAULT_VIEW")}
                  <small>{C("DEFAULT_HELP")}</small>
                </span>
              </button>
            </div>
            <footer className="actions">
              <button type="button" className="button ghost" disabled={busy} onClick={() => void restoreBase()}>
                {L("RESET")}
              </button>
              {selectedId ? (
                <>
                  <button type="button" className="button secondary" disabled={busy} onClick={() => void remove()}>
                    {L("DELETE")}
                  </button>
                  <button type="button" className="button secondary" disabled={busy} onClick={() => void save(false)}>
                    {L("UPDATE")}
                  </button>
                </>
              ) : null}
              <button type="button" className="button" disabled={busy} onClick={() => void save(true)}>
                {L(busy ? "SAVING" : "SAVE_NEW")}
              </button>
            </footer>
          </>
        ) : null}
        {success ? <Toast key={receipt} message={success} /> : null}
      </dialog>
    </div>
  );

  // Reintentar: vuelve a leer las vistas de la tabla abierta.
  async function reference(current: Table) {
    setBusy(true);
    const result = await api("GET", current.reportId);
    setBusy(false);
    failed(result.error);
    if (!result.error) setViews(result.views);
  }
}
